import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserCog, Save, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser, formatApiError } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingP, setSavingP] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    setSavingP(true);
    try { await api.put("/profile", { name, phone }); await refreshUser(); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingP(false); }
  };

  const changePw = async () => {
    if (!cur || !nw) return toast.error("Fill both fields");
    setSavingPw(true);
    try { await api.put("/profile/password", { current_password: cur, new_password: nw }); toast.success("Password changed"); setCur(""); setNw(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingPw(false); }
  };

  return (
    <div>
      <PageHeader title="Profile & Settings" subtitle="Manage your account" icon={UserCog}
        breadcrumb={["Home", "Profile"]} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-6 shadow-sm text-center">
          <Avatar className="h-24 w-24 mx-auto"><AvatarFallback className="bg-blue-600 text-white text-2xl font-bold">{initials}</AvatarFallback></Avatar>
          <div className="font-display font-bold text-lg mt-4 text-slate-900 dark:text-foreground">{user?.name}</div>
          <div className="text-sm text-slate-500 font-mono">{user?.email}</div>
          <div className="mt-2 inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold capitalize">{user?.role?.replace("_", " ")}</div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="mb-1.5 block">Full Name</Label><Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div><Label className="mb-1.5 block">Phone</Label><Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
            </div>
            <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingP} className="mt-4 bg-blue-600 hover:bg-blue-700">
              {savingP ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Changes
            </Button>
          </div>

          <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4 flex items-center gap-2"><KeyRound className="h-4 w-4" />Change Password</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="mb-1.5 block">Current Password</Label><Input data-testid="cur-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
              <div><Label className="mb-1.5 block">New Password</Label><Input data-testid="new-password" type="password" value={nw} onChange={(e) => setNw(e.target.value)} /></div>
            </div>
            <Button data-testid="change-pw-btn" onClick={changePw} disabled={savingPw} variant="outline" className="mt-4">
              {savingPw ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}Update Password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
